"use client";

import React from "react";
import { Client } from "@/lib/types/database";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";
import { InheritedContactMethodValue } from "@/components/shared/ContactMethodActions/InheritedContactMethodValue";
import { resolveInheritedContactMethods } from "@/lib/contacts/company-line/inherited-methods";
// The company fields rendered below are declared in a sibling module so the
// coverage guard can read them without importing this component's render tree.
// Keep the two in step: a new company row here needs its field added there.
export { CLIENT_CONTACT_INFO_COMPANY_FIELDS } from "./client-contact-info-company-fields";

interface ClientContactInfoProps {
  client: Client;
  variant?: "default" | "compact";
  showTitle?: boolean;
}

export const ClientContactInfo: React.FC<ClientContactInfoProps> = ({
  client,
  variant = "default",
  showTitle = true,
}) => {
  const isCompact = variant === "compact";
  const entityContext = {
    item: client as unknown as Record<string, unknown>,
    entityType: "contacts",
  };

  // A shared front desk / role mailbox lives on the COMPANY, so a person who
  // holds none of their own reads as having no phone or email at all — the gap
  // the 2026-08-21 remediation left on ~745 contact rows. Project the company's
  // shared methods here, badged and read-only; never written back onto the row.
  const inherited = resolveInheritedContactMethods(client, client.companies);
  const labelClass = `${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`;
  const valueClass = `${isCompact ? "text-sm" : "text-base"} text-card-foreground`;

  const content = (
    <div className={isCompact ? "space-y-1" : "space-y-3"}>
      <div>
        <p className={labelClass}>Email</p>
        <p className={valueClass}>
          {client.email?.trim() ? (
            <ContactMethodActions
              kind="email"
              value={client.email.trim()}
              entityContext={entityContext}
            />
          ) : (
            client.email ?? ""
          )}
        </p>
      </div>
      {inherited.emails.map((method) => (
        <div key={`inherited-email-${method.value}`}>
          <p className={labelClass}>Company email</p>
          <p className={valueClass}>
            <InheritedContactMethodValue method={method} fallbackContext={entityContext} />
          </p>
        </div>
      ))}
      {client.phone && (
        <div>
          <p className={labelClass}>Phone</p>
          <p className={valueClass}>
            <ContactMethodActions
              kind="phone"
              value={String(client.phone)}
              entityContext={entityContext}
            />
          </p>
        </div>
      )}
      {inherited.phones.map((method) => (
        <div key={`inherited-phone-${method.value}`}>
          <p className={labelClass}>Company phone</p>
          <p className={valueClass}>
            <InheritedContactMethodValue method={method} fallbackContext={entityContext} />
          </p>
        </div>
      ))}
      {client.title && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            Job Title
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground`}>
            {client.title}
          </p>
        </div>
      )}
      {client.companies?.website && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            Website
          </p>
          <a
            href={client.companies.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {client.companies.website}
          </a>
        </div>
      )}
      {client.companies?.industry && (
        <div>
          <p className={`${isCompact ? "text-xs" : "text-sm"} font-medium text-gray-500`}>
            Industry
          </p>
          <p className={`${isCompact ? "text-sm" : "text-base"} text-card-foreground`}>
            {client.companies.industry}
          </p>
        </div>
      )}
    </div>
  );

  if (!showTitle) {
    return content;
  }

  return (
    <div>
      <h2 className={`${isCompact ? "text-sm" : "text-lg"} font-semibold mb-4`}>
        {isCompact ? "Contact & Company" : "Contact Information"}
      </h2>
      {content}
    </div>
  );
};
